import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, EVENT_LABEL } from "../../lib/api";
import { fmtDateShort, fmtTime, initials } from "../../lib/format";
import { Spinner, EmptyState, Button } from "../../components/ui";
import { exportChoirAttendancePdf } from "../../lib/pdf";

// Backend contract: rate is already a 0–100 percentage.
const pct = (r) => (r == null ? null : Math.round(r));
import { Icon } from "../../components/icons";

const rangeStart = () => { const d = new Date(); d.setMonth(d.getMonth() - 3); return d.toISOString(); };
const rangeEnd = () => { const d = new Date(); d.setMonth(d.getMonth() + 6); return d.toISOString(); };

export default function AdminAttendance() {
  const navigate = useNavigate();
  const overview = useQuery({ queryKey: ["admin-attendance"], queryFn: () => api("/admin/attendance") });
  const eventsQ = useQuery({ queryKey: ["admin-events", "att"], queryFn: () => api(`/events?start=${encodeURIComponent(rangeStart())}&end=${encodeURIComponent(rangeEnd())}`) });

  const members = overview.data?.members || [];
  const events = Array.isArray(eventsQ.data) ? eventsQ.data : [];

  return (
    <>
      <div className="page-header" style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button className="icon-btn" onClick={() => navigate(-1)}><Icon name="chevron-back" size={22} /></button>
        <h1 className="t-h1" style={{ flex: 1 }}>Assiduidade do coro</h1>
        <Button className="auto sm ghost" icon="download" disabled={!members.length} data-testid="choir-attendance-pdf"
          onClick={() => exportChoirAttendancePdf(members.map((m) => ({ name: m.user.name, voice: m.user.voice, present: m.present, absent: (m.decided ?? m.total) - (m.present ?? 0), pending: m.pending, rate: pct(m.rate) })))}>PDF</Button>
      </div>

      {overview.isLoading ? (
        <Spinner />
      ) : (
        <div className="page ulc-fade" style={{ maxWidth: 820 }}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
            <Stat label="Eventos" value={overview.data?.total_events ?? 0} testid="att-total-events" />
            <Stat label="Decididos" value={overview.data?.decided ?? 0} color="var(--success)" testid="att-decided" />
            <Stat label="Por confirmar" value={overview.data?.pending ?? 0} color="var(--warning)" testid="att-pending" />
          </div>

          <div className="t-section" style={{ marginBottom: 12 }}>Por evento</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 28 }}>
            {events.length ? events.map((e) => (
              <div key={e.id} className="card" onClick={() => navigate(`/admin/attendance/${e.id}`)} style={{ display: "flex", alignItems: "center", gap: 12, padding: 14, cursor: "pointer" }} data-testid={`admin-attendance-event-${e.id}`}>
                <div style={{ width: 40, height: 40, borderRadius: "var(--r-sm)", background: "var(--surface-3)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name={e.type === "concerto" ? "musical-notes" : "calendar-outline"} size={18} color="var(--on-surface-3)" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "var(--f-medium)", fontSize: 15 }}>{e.title || EVENT_LABEL[e.type] || "Evento"}</div>
                  {e.starts_at ? <div className="t-hint">{fmtDateShort(e.starts_at)} · {fmtTime(e.starts_at)}</div> : null}
                </div>
                {e.pending_count ? <span className="pill todo">{e.pending_count} POR CONFIRMAR</span> : null}
                <Icon name="chevron-forward" size={18} color="var(--muted)" />
              </div>
            )) : <EmptyState icon="calendar-outline" text="Sem eventos no período." />}
          </div>

          <div className="t-section" style={{ marginBottom: 12 }}>Por membro</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {members.length ? members.map((m) => {
              const rate = pct(m.rate);
              return (
                <div key={m.user.id} className="card" style={{ display: "flex", alignItems: "center", gap: 12, padding: 12 }} data-testid={`att-member-${m.user.id}`}>
                  <div style={{ width: 40, height: 40, borderRadius: 999, background: "var(--brand-tertiary)", color: "var(--on-brand)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--f-medium)", fontSize: 14 }}>{initials(m.user.name)}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "var(--f-medium)", fontSize: 15 }}>{m.user.name}</div>
                    <div className="t-hint">{m.user.voice} · {m.present}/{m.total} presenças{m.pending ? ` · ${m.pending} por confirmar` : ""}</div>
                  </div>
                  {rate != null ? <span className="pill" style={{ background: rate >= 75 ? "var(--success)" : rate >= 50 ? "var(--warning)" : "var(--error)", color: "var(--on-surface)" }}>{rate}%</span> : <span className="t-hint">—</span>}
                </div>
              );
            }) : <EmptyState icon="stats" text="Ainda sem registos de assiduidade." />}
          </div>
        </div>
      )}
    </>
  );
}

function Stat({ label, value, color, testid }) {
  return (
    <div className="card" style={{ padding: 16, minWidth: 120, flex: 1 }} data-testid={testid}>
      <div style={{ fontSize: 30, fontFamily: "var(--f-medium)", color: color || "var(--on-surface)" }}>{value}</div>
      <div className="t-hint" style={{ textTransform: "uppercase", letterSpacing: 1 }}>{label}</div>
    </div>
  );
}
